import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { api, gbp } from "@/lib/api";
import { toast } from "sonner";

/**
 * ConsultationChooserModal — shown when a client adds the standalone
 * "Consultation" treatment to their cart. Forces them to pick which
 * treatment they'd like to discuss (that choice is later used to auto-
 * apply the £50 credit to their next matching treatment booking).
 */
export default function ConsultationChooserModal({ treatment, onClose, onConfirm }) {
  const [targets, setTargets] = useState([]);
  const [key, setKey] = useState("");

  useEffect(() => {
    api.get("/consultation-targets")
      .then((r) => setTargets(r.data || []))
      .catch(() => toast.error("Could not load consultation options"));
  }, []);

  const submit = () => {
    if (!key) { toast.error("Please choose a treatment"); return; }
    const t = targets.find((x) => x.key === key);
    onConfirm({
      consultation_target_key: key,
      consultation_target_label: t ? t.label : "",
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="consultation-chooser">
      <div className="w-full max-w-lg bg-[#FAF9F6] border hairline" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center px-6 py-4 border-b hairline">
          <span className="mono-label">Consultation · £50 secures your appointment</span>
          <button onClick={onClose} data-testid="consultation-chooser-close" className="text-stone-500 hover:text-stone-900"><X size={18} /></button>
        </div>
        <div className="px-6 py-6">
          <h3 className="font-serif text-2xl text-stone-900">Which treatment are you interested in?</h3>
          <p className="mt-2 text-sm text-stone-600 leading-relaxed">
            Your £50 consultation fee secures your dedicated appointment time and, following your consultation, is redeemable against the treatment you select below.
          </p>
          <label className="mono-label mt-6 block">Treatment</label>
          <select
            data-testid="consultation-target-select"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            className="mt-2 w-full border hairline bg-white p-3 text-sm focus:outline-none focus:border-stone-900"
          >
            <option value="">— Please select —</option>
            {targets.map((t) => (
              <option key={t.key} value={t.key} data-testid={`consultation-target-option-${t.key}`}>{t.label}</option>
            ))}
          </select>
          <div className="mt-6 flex items-center justify-between gap-3">
            <span className="font-serif text-2xl text-stone-900">{gbp(treatment.price)}</span>
            <button
              data-testid="consultation-chooser-confirm"
              onClick={submit}
              disabled={!key}
              className="border border-stone-900 bg-stone-900 text-white px-6 py-3 mono-label hover:bg-[#C5A059] transition disabled:opacity-30"
              style={{ color: "#fff" }}
            >
              Add Consultation
            </button>
          </div>
          <p className="mt-4 text-[11px] text-stone-500 leading-relaxed">
            You pay the full £50 at checkout. Cancel 48+ hours before for a full refund or one reschedule. Inside 48 hours the fee is retained.
          </p>
        </div>
      </div>
    </div>
  );
}
